import { defineStore } from 'pinia';
import {
  LocationDTO,
  RateDTO,
  RoomTypeDTO,
  CreateReservationRequestDTO,
  BrowserDataDTO,
  ErrorResponseDTO,
  CheckVoucherRequestDTO,
  BookConfigurationDTO,
  OfferDTO,
  BookingIncentiveDTO,
  ReservationStatusResponseDTO,
  VisitorLevyDTO,
} from 'src/services/api/data-contracts';
import { IRoom, ISearchRoom } from 'src/types/common';
import {
  ReservationItem,
  Reservation,
  CreateBookingProcessStep,
  NavigationPage,
  Guest,
  ReservationPaymentType,
} from 'src/types/createBooking';
import { usePropertyStore } from 'src/stores/propertyStore';
import { useSearchAvailabilityStore } from 'src/stores/searchAvailabilityStore';
import { date, Platform } from 'quasar';
import useDateHelperUtils from 'src/composables/dateHelperUtils.ts';
import { useServiceStore } from 'src/stores/serviceStore';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { Notify } from 'quasar';
import { i18n } from 'boot/i18n';
import { AxiosError, AxiosResponse } from 'axios';
import useAnalyticUtils from 'src/composables/analyticUtils';
import { useAuthStore } from './authStore';
import useRateHelperUtils from 'src/composables/rateHelperUtils';
import { useAvailabilityStore } from 'src/stores/availabilityStore';
import { useSelectStateStore } from 'src/stores/selectStateStore';
import { useRestaurantTableStore } from './restaurantTableStore';
import { useRoomTypeAvailabilityStore } from './roomTypeAvailabilityStore';

const { isValid, formatDate } = date;

const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useNewBookingStore = defineStore('newBookingStore', {
  state: () => ({
    propertyId: null as number | null,
    config: null as BookConfigurationDTO | null,

    locations: [] as Array<LocationDTO> | undefined,
    offers: [] as Array<OfferDTO> | undefined,
    incentives: [] as Array<BookingIncentiveDTO> | undefined,

    maxNumberOfRoomsOnBooking: null as number,
    showRoomConfigurationQuestion: false as boolean,

    hidePromoCodeOnLandingPage: false as boolean,
    noAvailabilityMessage: '' as string,

    giftServiceProviderActive: false as boolean,
    giftServiceProviderName: null as string,

    enableUserChoiceOfPaymentMethod: false as boolean,

    enablePayByCompanyInvoice: false as boolean,
    hasCustomTermsAndConditions: false as boolean,
    customTermsAndConditionsLabel: null as string,
    customTermsAndConditionsText: null as string,
    onlyUseCustomTermsAndConditionsForPayByCompanyInvoice: false as boolean,

    showMinimumLengthOfStayNotification: false as boolean,
    showDisabledDatesOnCalendar: false as boolean,
    showRoomTypeOccupancy: true as boolean,

    sameDayBookingRestrictionEnabled: false as boolean,
    sameDayBookingRestrictionHour: null as number | null,
    sameDayBookingRestrictionMinute: null as number | null,
    sameDayBookingRestrictionMessage: null as string | null,

    languageCode: 'en' as string,
    fromDate: null as string | null,
    toDate: null as string | null,
    promoCode: null as string | null,
    isPromoCodeValid: true as boolean,
    hasCheckedPromoCodeIsValid: false as boolean,
    ratePlanCode: null as string | null,
    searchControlRooms: [] as Array<ISearchRoom>,
    location: null as LocationDTO | null,
    roomTypeId: null as number | null,

    navigationPage: NavigationPage.Search as NavigationPage,
    processStep: CreateBookingProcessStep.None as CreateBookingProcessStep,

    propertyControlTitle: 'Choose your property' as string | null,
    propertyControlSubTitle: null as string | null,
    canOpenPropertySelection: false as boolean,
    showPropertySelection: false as boolean,
    showDateCaptureDialog: false as boolean,
    showRoomsCaptureDialog: false as boolean,
    canBookRooms: true as boolean,
    restrictedBookingWarning: null as string | null,
    showRestrictedBookingWarning: false as boolean,

    hopGiftVoucherCode: undefined as string | undefined,
    hopGiftDiscountAmount: null as number | null,
    checkingVoucherCode: false as boolean,
    hopGiftMessage: null as string,
    hopGiftShowContactUs: false as boolean,
    reservation: null as Reservation | null,
    paymentType:
      ReservationPaymentType.StoreCard as ReservationPaymentType | null,

    totalCostAtCheckout: null as number,
    totalTaxesAtCheckout: null as number,
    visitorLevy: null as VisitorLevyDTO | null,

    // The list of rooms for the result set.
    rooms: [] as Array<ISearchRoom>,
    activeRoom: null as IRoom, // The active room that is being worked on
    activeReservationItem: null as ReservationItem | null, // The active ResX that is being worked on
    reservationItems: [] as ReservationItem[],
    isCreatingBooking: false as boolean,
    isAppView: false as boolean,
  }),
  getters: {
    canSearch(): boolean {
      let isValid = false;

      isValid = this.propertyId != undefined;
      isValid =
        isValid && this.fromDate != undefined && this.toDate != undefined;
      isValid = isValid && this.searchControlRooms.length > 0;
      isValid = isValid && !this.maxNumberOfRoomsOnBookingExceeded;

      return isValid;
    },
    promoCodeButtonText(): string {
      const code = this.promoCode;
      if (code == null || code == '' || !this.hasCheckedPromoCodeIsValid) {
        return t('search.promoCodeLabel');
      } else if (!this.isPromoCodeValid) {
        return t('search.promoCodeInvalid');
      }
      return t('search.promoCodeEntered');
    },
    promoCodeButtonIconName(): string {
      const code = this.promoCode;
      if (code == null || code == '' || !this.hasCheckedPromoCodeIsValid) {
        return 'add';
      } else if (!this.isPromoCodeValid) {
        return 'cancel';
      }
      return 'check';
    },
    promoCodeButtonTextColor(): string {
      const code = this.promoCode;
      if (code == null || code == '' || !this.hasCheckedPromoCodeIsValid) {
        return '';
      } else if (!this.isPromoCodeValid) {
        return 'text-warning';
      }
      return 'text-positive';
    },
    isDateSelectionValid(): boolean {
      let datesAreValid = false;
      datesAreValid = date.isValid(this.fromDate) && date.isValid(this.toDate);
      return datesAreValid;
    },
    isFromDateSelectionValid(): boolean {
      let datesAreValid = false;
      datesAreValid = date.isValid(this.fromDate);
      return datesAreValid;
    },
    isToDateSelectionValid(): boolean {
      let datesAreValid = false;
      datesAreValid = date.isValid(this.toDate);
      return datesAreValid;
    },
    numberOfNights(): number {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.calculateNumberOfNights(
        this.fromDate,
        this.toDate,
      );
    },
    numberOfNightsDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatNumberOfNightsDisplay(this.numberOfNights);
    },
    fromDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(this.fromDate);
    },
    toDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(this.toDate);
    },
    maxNumberOfRoomsForBooking(): number | null {
      return this.maxNumberOfRoomsOnBooking;
    },
    maxNumberOfRoomsOnBookingExceeded(): boolean {
      const numberOfRooms = this.rooms.length;
      const maxNumberOfRooms = this.maxNumberOfRoomsForBooking;

      return maxNumberOfRooms != null
        ? numberOfRooms > maxNumberOfRooms
        : false;
    },
    numberOfRooms(): number {
      return this.rooms.length;
    },
    hasRooms(): boolean {
      return this.rooms.length > 0;
    },
    hasSearchControlRooms(): boolean {
      return this.searchControlRooms.length > 0;
    },
    searchRoomSummary(): string {
      let roomSummary = '';
      const roomCount = this.searchControlRooms.length;
      if (roomCount == 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitSingular');
      } else if (roomCount > 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitPlural');
      }

      return roomSummary;
    },
    searchGuestSummary(): string {
      let guestSummary = '';
      const guestCount = this.searchControlRooms.reduce((total, room) => {
        return (
          total +
          room.NumberOfAdults +
          room.NumberOfChildren +
          room.NumberOfInfants
        );
      }, 0);
      if (guestCount == 1) {
        guestSummary = guestCount + ' ' + t('Guest');
      } else if (guestCount > 1) {
        guestSummary = guestCount + ' ' + t('Guests');
      }

      return guestSummary;
    },

    roomSummary(): string {
      let roomSummary = '';
      const roomCount = this.reservationItems.length;
      if (roomCount == 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitSingular');
      } else if (roomCount > 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitPlural');
      }

      return roomSummary;
    },
    guestSummary(): string {
      let guestSummary = '';
      const guestCount = this.reservationItems.reduce((total, room) => {
        return (
          total +
          room.NumberOfAdults +
          room.NumberOfChildren +
          room.NumberOfInfants
        );
      }, 0);
      if (guestCount == 1) {
        guestSummary = guestCount + ' ' + t('Guest');
      } else if (guestCount > 1) {
        guestSummary = guestCount + ' ' + t('Guests');
      }

      return guestSummary;
    },
    hasReservationItems(): boolean {
      return this.reservationItems.length > 0;
    },
    totalRoomCost(): number {
      const roomCost = this.reservationItems.reduce((total, room) => {
        return total + room.totalRoomCost;
      }, 0);
      return roomCost;
    },
    totalDiscountRoomCost(): number {
      const roomCost = this.reservationItems.reduce((total, room) => {
        return total + room.totalDiscountRoomCost;
      }, 0);
      return roomCost;
    },
    totalDiscount(): number {
      return this.totalDiscountRoomCost - this.totalRoomCost;
    },
    totalExtrasCost(): number {
      const extrasCost = this.reservationItems.reduce((total, room) => {
        return total + room.totalServiceCost;
      }, 0);
      return extrasCost;
    },
    totalCost(): number {
      const hgAmount = this.hopGiftDiscountAmount ?? 0;
      const hopGiftAmount = hgAmount;

      const totalCost = this.reservationItems.reduce((total, room) => {
        return total + room.totalCost;
      }, 0);
      return totalCost - hopGiftAmount;
    },
    totalVatAmount(): number {
      const totalVatAmount = this.reservationItems.reduce((total, room) => {
        return total + room.totalVatAmount;
      }, 0);
      return totalVatAmount;
    },
    totalPaymentAmount(): number {
      const totalPayment = this.reservationItems.reduce((total, room) => {
        const newTotal = total + room.totalPaymentAmount;
        return newTotal;
      }, 0);

      return totalPayment;
    },
    totalPaymentAmountForDisplay(): number {
      let totalPayment = this.totalPaymentAmount;
      totalPayment = totalPayment - this.hopGiftDiscountAmount;
      return totalPayment;
    },
    canPayForBooking(): boolean {
      return this.totalPaymentAmountForDisplay > 0;
    },
    paymentTypeOptions() {
      return [
        { label: 'Pay Online', value: ReservationPaymentType.StoreCard },
        {
          label: 'Pay by Invoice',
          value: ReservationPaymentType.PayByIndividualInvoice,
        },
      ];
    },
    payByCard(): boolean {
      return (
        this.paymentType == ReservationPaymentType.StoreCard ||
        this.paymentType == ReservationPaymentType.PayByCard
      );
    },
    payByIndividualInvoice(): boolean {
      return this.paymentType == ReservationPaymentType.PayByIndividualInvoice;
    },
    payByCompanyInvoice(): boolean {
      return this.paymentType == ReservationPaymentType.PayByCompanyInvoice;
    },
    payByPayStack(): boolean {
      return this.paymentType == ReservationPaymentType.PayByPayStack;
    },
    showCustomTermsAndConditions(): boolean {
      return (
        this.hasCustomTermsAndConditions &&
        (!this.onlyUseCustomTermsAndConditionsForPayByCompanyInvoice ||
          (this.onlyUseCustomTermsAndConditionsForPayByCompanyInvoice &&
            this.payByCompanyInvoice))
      );
    },
    canConfirmBooking(): boolean {
      return (
        this.reservation.booker.arePersonalDetailsValid &&
        this.reservation.booker.areAddressDetailsValid &&
        (this.payByPayStack ||
          (this.payByCard && this.reservation.creditCard.isValid) ||
          this.payByIndividualInvoice ||
          (this.payByCompanyInvoice &&
            this.reservation.invoiceDetailsCaptured)) &&
        this.reservation.agreeToTermsAndConditions &&
        (!this.showCustomTermsAndConditions ||
          (this.showCustomTermsAndConditions &&
            this.reservation.hasAcceptedCustomTermsAndConditions)) &&
        !this.isCreatingBooking
      );
    },

    canSelectIndividualPaymentMethod(): boolean {
      const as = useAuthStore();

      return (
        this.enableUserChoiceOfPaymentMethod &&
        as.isUserLoggedIn &&
        as.user.canChoosePaymentMethod &&
        !this.payByPayStack &&
        !this.payByCompanyInvoice
      );
    },
    isActiveReservationItemLeadGuestValid(): boolean {
      let isValid = false;
      const showRoomConfigurationQuestion = this.showRoomConfigurationQuestion;
      if (showRoomConfigurationQuestion) {
        isValid = this.activeReservationItem?.roomConfiguration != null;
      } else {
        isValid = true;
      }

      isValid = isValid && this.activeReservationItem?.leadGuest.isValid;

      return isValid;
    },
    areGuestsValid(): boolean {
      const guestsAreValid = this.reservationItems.reduce((value, g) => {
        return value && g.leadGuest.isValid;
      }, true);
      return guestsAreValid;
    },
    areExtrasComplete(): boolean {
      const areComplete = this.reservationItems.reduce((value, g) => {
        return value && g.haveSeenServices;
      }, true);
      return areComplete;
    },
    onlyPayByCompanyInvoiceRatePlans(): boolean {
      const canPayByCompanyInvoice = this.reservationItems.reduce(
        (value, g) => {
          return value && g.ratePlan.CanPayByCompanyInvoice;
        },
        true,
      );
      return canPayByCompanyInvoice;
    },
  },
  actions: {
    getBookConfig(propertyId: number) {
      this.propertyId = propertyId;
      return apiService.bookConfig(propertyId).then((response) => {
        this.config = response.data;

        const propertyConfigData = response.data;

        this.hidePromoCodeOnLandingPage =
          propertyConfigData.HidePromoCodeOnLandingPage;

        this.locations = propertyConfigData.Locations ?? [];

        this.noAvailabilityMessage = propertyConfigData.NoAvailabilityMessage;

        this.maxNumberOfRoomsOnBooking =
          propertyConfigData.MaxNumberOfRoomsOnBooking;

        this.showRoomConfigurationQuestion =
          propertyConfigData.ShowRoomConfigurationQuestion;

        this.offers = propertyConfigData.Offers;
        this.incentives = propertyConfigData.BookingIncentives;

        this.giftServiceProviderActive =
          propertyConfigData.GiftServiceProviderConfig.Active;
        this.giftServiceProviderName =
          propertyConfigData.GiftServiceProviderConfig.Name;

        this.enableUserChoiceOfPaymentMethod =
          propertyConfigData.EnableUserChoiceOfPaymentMethod;

        this.enablePayByCompanyInvoice =
          propertyConfigData.EnablePayByCompanyInvoice;

        this.hasCustomTermsAndConditions =
          propertyConfigData.HasCustomTermsAndConditions;
        this.customTermsAndConditionsLabel =
          propertyConfigData.CustomTermsAndConditionsLabel;
        this.customTermsAndConditionsText =
          propertyConfigData.CustomTermsAndConditionsText;
        this.onlyUseCustomTermsAndConditionsForPayByCompanyInvoice =
          propertyConfigData.OnlyUseCustomTermsAndConditionsForPayByCompanyInvoice;

        const restaurantTableStore = useRestaurantTableStore();
        restaurantTableStore.active =
          propertyConfigData.RestaurantBookingServiceActive;
        restaurantTableStore.defaultRestaurant =
          propertyConfigData.RestaurantBookingDefaultRestaurant;
        restaurantTableStore.serviceType =
          propertyConfigData.RestaurantBookingServiceType;

        this.showMinimumLengthOfStayNotification =
          propertyConfigData.ShowMinimumLengthOfStayNotification;
        this.showRoomTypeOccupancy = propertyConfigData.ShowRoomTypeOccupancy;
        this.showDisabledDatesOnCalendar =
          propertyConfigData.ShowDisabledDatesOnCalendar;

        this.sameDayBookingRestrictionEnabled =
          propertyConfigData.SameDayBookingRestrictionEnabled;
        this.sameDayBookingRestrictionHour =
          propertyConfigData.SameDayBookingRestrictionHour;
        this.sameDayBookingRestrictionMinute =
          propertyConfigData.SameDayBookingRestrictionMinute;
        this.sameDayBookingRestrictionMessage =
          propertyConfigData.SameDayBookingRestrictionMessage;

        if (propertyConfigData.RestaurantBookingServiceActive) {
          restaurantTableStore.getRestaurants(propertyConfigData.PropertyId);
        }
      });
    },

    editReservationItem(
      item: ReservationItem,
      processStep: CreateBookingProcessStep,
    ) {
      this.activeReservationItem = item;
      this.setProcessStep(processStep);

      this.activeRoom = item;

      // When editing
      if (
        this.activeReservationItem.roomType &&
        this.activeReservationItem.ratePlan
      ) {
        const selectStateStore = useSelectStateStore();
        const searchAvailabilityStore = useSearchAvailabilityStore();

        const selectedRoomType =
          searchAvailabilityStore.availableRoomTypesForActiveRoomSelection.find(
            (v) =>
              v.RoomType.RoomTypeId ==
              this.activeReservationItem.roomType.RoomTypeId,
          );

        selectStateStore.roomType = selectedRoomType;
        if (selectedRoomType) {
          const selectedRatePlan = selectedRoomType.Rates.find(
            (v) =>
              v.RatePlanId == this.activeReservationItem.ratePlan.RatePlanId,
          );

          selectStateStore.rate = selectedRatePlan;
        }
      }
    },
    copyBookerDetailsToCreditCard() {
      if (!this.reservation.creditCard.creditCardName) {
        this.reservation.creditCard.creditCardName =
          this.reservation.booker.fullname;
        this.reservation.creditCard.cardHolderEmail =
          this.reservation.booker.emailAddress;
      }
    },

    checkStatus(
      sessionKey: string,
    ): Promise<AxiosResponse<ReservationStatusResponseDTO, string>> {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      const statusPromise = apiService.reservationStatus({
        propertyId: propertyId,
        referenceNumber: sessionKey,
      });

      statusPromise.then((confirmationResponse) => {
        if (confirmationResponse.data != null) {
          if (!confirmationResponse.data.Success) {
            Notify.create({
              color: 'negative',
              position: 'top',
              message: confirmationResponse.data.Message,
            });
          }
        }
      });

      return statusPromise;
    },

    createBooking() {
      this.isCreatingBooking = true;

      const booker = this.reservation.booker.toDTO();
      const cc = this.payByCard ? this.reservation.creditCard.toDTO() : null;

      let paymentType = this.paymentType;
      const paymentAmount = this.totalPaymentAmount;
      if (
        paymentAmount > 0 &&
        paymentType == ReservationPaymentType.StoreCard
      ) {
        paymentType = ReservationPaymentType.PayByCard;
      }

      const analyticUtils = useAnalyticUtils();
      const analyticsData = analyticUtils.getAnalyticsData();

      const reservationDto = {
        LanguageCode: 'en',
        IsAppBooking: this.isAppView,
        PropertyId: this.propertyId,
        DateFrom: this.fromDate,
        DateTo: this.toDate,
        Notes: this.reservation.specialRequests,
        Comment: this.reservation.comment,
        Booker: booker,
        CardDetails: cc,
        PaymentAmount: paymentAmount,
        PaymentType: paymentType.toString(),
        AccountId: this.reservation.company?.AccountId,
        PONumber: this.reservation.poNumber,
        PromoCode: this.promoCode,
        HopGiftVoucherCode: this.hopGiftVoucherCode,
        Rooms: this.reservationItems.map((ri) => ri.toDTO()),
        BookingMetadata: {
          'gclid': analyticsData?.gclid,
          'wbraid': analyticsData?.wbraid,
          'gbraid': analyticsData?.gbraid,
        },
        BrowserData: {
          ColorDepthInBits: screen.colorDepth,
          JavaScriptEnabled: true,
          BrowserLanguage: navigator.language,
          ScreenHeight: screen.height,
          ScreenWidth: screen.width,
          UserAgent: navigator.userAgent,
          TimezoneOffSet: new Date().getTimezoneOffset().toString(),
        } as BrowserDataDTO,
      } as CreateReservationRequestDTO;

      const createBookingPromise = apiService.reservationCreate(reservationDto);

      createBookingPromise.catch(
        (createBookingAxiosError: AxiosError<ErrorResponseDTO>) => {
          const message =
            createBookingAxiosError.response.data?.Message ??
            'Failed to create booking';

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: message,
          });

          this.isCreatingBooking = false;
        },
      );

      return createBookingPromise;
    },
    setProcessStep(processStep: CreateBookingProcessStep) {
      this.processStep = processStep;
      if (Platform.is.mobile) {
        this.scrollToTop();
      }
    },
    scrollToTop() {
      // TODO Make this mobile vs desktop friendly.
      window.scrollTo(0, 0);
    },
    /// Find a reservation item based on Room Id, with an optional selector
    getReservationItem(
      roomId: string,
      selector: (ri: ReservationItem) => boolean = () => {
        return true;
      },
    ): ReservationItem | null {
      const items = this.reservationItems.filter((y) => {
        return y.roomId == roomId && selector(y);
      });
      if (items.length > 0) {
        return items[0];
      }
      return null;
    },
    getNextReservationItem(
      currentReservationItem: ReservationItem,
      selector: (ri: ReservationItem) => boolean,
    ): ReservationItem | null {
      let nextResX: ReservationItem = null;
      let index = -1;

      const getNextResX = (i: number) => {
        let nextX = this.reservationItems[i];
        if (nextX != null) {
          const resX = this.getReservationItem(nextX.roomId, selector);
          if (resX == null) {
            nextX = getNextResX(i + 1);
          } else {
            nextX = resX;
          }
        }

        return nextX;
      };

      if (currentReservationItem == null) {
        index = 0;
        nextResX = getNextResX(index);
      } else {
        index = this.rooms.findIndex((y) => {
          return y.roomId == currentReservationItem?.roomId;
        });

        if (index > -1) {
          nextResX = getNextResX(index + 1);
        }
      }

      return nextResX;
    },
    next() {
      const router = this.router;
      const propertyStore = usePropertyStore();
      if (
        this.processStep == CreateBookingProcessStep.RoomTypeSelection ||
        this.processStep == CreateBookingProcessStep.RatePlanSelection
      ) {
        this.scrollToTop();
        if (this.rooms.length == this.reservationItems.length) {
          this.processStep = CreateBookingProcessStep.UpgradeSelection;
          this.next();
        } else if (this.rooms.length > this.reservationItems.length) {
          this.setNextActiveRoom();
        }
      } else if (
        this.processStep == CreateBookingProcessStep.UpgradeSelection
      ) {
        this.scrollToTop();

        const ari = this.activeReservationItem;
        if (ari != null) {
          ari.checkForUpgrades();
        }

        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.haveSeenUpgrades &&
          this.activeReservationItem.hasUpgrades
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              ri.checkForUpgrades(); // check for upgrades here before selecting.
              return !ri.haveSeenUpgrades && ri.hasUpgrades;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.processStep = CreateBookingProcessStep.Occupancy;
          this.next();
        }
      } else if (this.processStep == CreateBookingProcessStep.Occupancy) {
        this.scrollToTop();
        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.leadGuest.isValid
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return !ri.leadGuest.isValid;
            },
          );
        }
        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.processStep = CreateBookingProcessStep.Extras;
          this.next();
        }
      } else if (this.processStep == CreateBookingProcessStep.Extras) {
        this.scrollToTop();
        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.hasSelectableServices &&
          !this.activeReservationItem.haveSeenServices
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return ri.hasSelectableServices && !ri.haveSeenServices;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.processStep = CreateBookingProcessStep.BookingSummary;
          this.next();
        }
      } else if (this.processStep == CreateBookingProcessStep.BookingSummary) {
        if (Platform.is.mobile) {
          this.scrollToTop();
        } else if (Platform.is.desktop) {
          const currentRoute = router.currentRoute;
          if (currentRoute.value.name != 'Checkout') {
            router.push({
              name: 'Checkout',
              params: { propertyCode: propertyStore.propertyCode },
            });
          }
        }
      }
    },
    setNextActiveReservationItem(nextItem?: ReservationItem) {
      const currentResX = this.activeReservationItem;
      let nextResX: ReservationItem = null;

      if (nextItem != null) {
        nextResX = nextItem;
      } else {
        if (currentResX == null) {
          nextResX = this.reservationItems[0];
        } else {
          const index = this.rooms.findIndex((y) => {
            return y.roomId == currentResX.roomId;
          });

          if (index > -1) {
            const nextIndex = index + 1;

            const getNextResX = (i: number) => {
              let nextX = this.reservationItems[i];
              if (nextX != null) {
                const resX = this.getReservationItem(nextX?.roomId);
                if (resX == null) {
                  nextX = getNextResX(i + 1);
                } else {
                  nextX = resX;
                }
              }

              return nextX;
            };

            nextResX = getNextResX(nextIndex);
          }
        }
      }
      this.activeReservationItem = nextResX;

      return nextResX;
    },
    resetActiveRoom() {
      this.activeRoom = null;
    },
    setActiveRoom(item: ReservationItem) {
      this.activeRoom = {
        roomId: item.roomId,
        roomNumber: item.roomNumber,
        NumberOfAdults: item.NumberOfAdults,
        NumberOfChildren: item.NumberOfChildren,
        NumberOfInfants: item.NumberOfInfants,
      } as IRoom;
    },
    setNextActiveRoom() {
      let nextRoom: IRoom = null;
      const currentRoom = this.activeRoom;

      if (currentRoom == null) {
        nextRoom = this.rooms[0];
      } else {
        const index = this.rooms.findIndex((y) => {
          return y.roomId == currentRoom.roomId;
        });

        if (index > -1) {
          const nextIndex = index + 1;

          const getNextRoomWithoutAResX = (i: number) => {
            nextRoom = this.rooms[i];
            const resX = this.getReservationItem(nextRoom?.roomId);
            if (resX != null) {
              nextRoom = getNextRoomWithoutAResX(i + 1);
            }

            return nextRoom;
          };

          nextRoom = getNextRoomWithoutAResX(nextIndex);
        }
      }

      this.activeRoom = nextRoom;
    },
    calculateRoomTypeLowestRates() {
      if (this.activeRoom != null) {
        const rateHelperUtils = useRateHelperUtils();
        const searchAvailabilityStore = useRoomTypeAvailabilityStore();

        for (
          let index = 0;
          index < searchAvailabilityStore.RoomTypes.length;
          index++
        ) {
          const element = searchAvailabilityStore.RoomTypes[index];
          element.LowestRate = rateHelperUtils.calculateLowestRoomRate(
            this.activeRoom,
            element.Rates,
            false,
          );
          element.OriginalLowestRate = rateHelperUtils.calculateLowestRoomRate(
            this.activeRoom,
            element.Rates,
            true,
          );
        }
      }
    },

    resetBasket() {
      this.reservationItems.splice(0); // maintain reactivity.
    },
    resetActiveReservationItem() {
      this.activeReservationItem = null;
    },

    addReservationItem(
      room: IRoom,
      roomType: RoomTypeDTO,
      rate: RateDTO,
    ): ReservationItem | null {
      const analyticUtils = useAnalyticUtils();
      const ps = usePropertyStore();
      const searchAvailabilityStore = useSearchAvailabilityStore();

      const ri = new ReservationItem();
      ri.DateOfArrival = this.fromDate;
      ri.DateOfDeparture = this.toDate;

      ri.roomTypeQuantityChecker = (roomTypeId: number) => {
        return searchAvailabilityStore.getAvailability(roomTypeId);
      };
      ri.VatSettings = ps.vatSettings;

      ri.roomId = room.roomId;
      ri.roomNumber = room.roomNumber;
      ri.leadGuest = new Guest();

      ri.roomHeader = t('roomHeader', {
        roomNumber: room.roomNumber.toString(),
      });

      ri.NumberOfAdults = room.NumberOfAdults;
      ri.NumberOfChildren = room.NumberOfChildren;
      ri.NumberOfInfants = room.NumberOfInfants;

      ri.RatePlanId = rate.RatePlanId;
      ri.RoomTypeId = roomType.RoomTypeId;
      ri.roomType = roomType;

      ri.SetRate(rate);

      const availableRoomsForThisRoomType =
        searchAvailabilityStore.getAvailability(roomType.RoomTypeId);

      if (availableRoomsForThisRoomType >= 1) {
        this.reservationItems.push(ri);

        searchAvailabilityStore.decreaseAvailabilityByOne(ri.RoomTypeId);

        this.reservationItems.sort((a, b) => {
          return a.roomNumber > b.roomNumber ? 1 : -1;
        });

        const serviceStore = useServiceStore();
        serviceStore.getNewServices(ri);

        analyticUtils.trackAddRoomToBasket(ps.propertyCode, ps.currency, ri);

        ri.checkForUpgrades();

        return ri;
      } else {
        // Not enough rooms available.
        return null;
      }
    },
    removeReservationItem(reservationItem: ReservationItem) {
      const analyticUtils = useAnalyticUtils();
      const ps = usePropertyStore();

      const searchAvailabilityStore = useSearchAvailabilityStore();

      const index = this.reservationItems.indexOf(reservationItem);
      if (index > -1) {
        this.reservationItems.splice(index, 1);

        searchAvailabilityStore.increaseAvailabilityByOne(
          reservationItem.RoomTypeId,
        );
      }

      analyticUtils.trackRemoveRoomFromBasket(
        ps.propertyCode,
        ps.currency,
        reservationItem,
      );
    },
    setDates(fromDate: string, toDate: string) {
      this.fromDate = fromDate;
      this.toDate = toDate;

      if (this.isFromDateSelectionValid && !this.isToDateSelectionValid) {
        const dt = useDateHelperUtils();
        this.toDate = dt.calculateToDate(this.fromDate, 1);
      }

      setTimeout(() => {
        this.checkBookingTimeRestrictions();

        const availabilityStore = useAvailabilityStore();
        availabilityStore.generateAlternativeStays();
      }, 100);
    },
    checkBookingTimeRestrictions() {
      this.canBookRooms = true;
      this.restrictedBookingWarning = '';
      this.showRestrictedBookingWarning = false;

      if (this.sameDayBookingRestrictionEnabled) {
        const bookingDate = this.fromDate;

        if (
          isValid(bookingDate) &&
          bookingDate != null &&
          bookingDate == formatDate(new Date(), 'YYYY-MM-DD')
        ) {
          const now = new Date();
          const time = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            this.sameDayBookingRestrictionHour,
            this.sameDayBookingRestrictionMinute,
            0,
          );

          if (now >= time) {
            this.canBookRooms = false;
          }

          if (!this.canBookRooms) {
            this.restrictedBookingWarning =
              this.sameDayBookingRestrictionMessage;
            this.showRestrictedBookingWarning = true;
          }
        }
      }
    },
    setRooms(rooms: Array<ISearchRoom>) {
      this.rooms = rooms.map(({ ...ele }) => {
        return ele;
      });
    },
    setSearchRooms(rooms: Array<ISearchRoom>) {
      this.searchControlRooms = rooms.map(({ ...ele }) => {
        return ele;
      });
    },
    setLocation(location: LocationDTO) {
      this.location = location;
      if (this.location != null) {
        this.propertyControlTitle = this.location.Name;
        this.propertyControlSubTitle = this.location.Address;
      } else {
        const propertyStore = usePropertyStore();
        this.propertyControlTitle = propertyStore.propertyName;
        this.propertyControlSubTitle = '';
      }

      if (Platform.is.desktop) {
        this.showPropertySelection = false;
      }
    },
    checkHopGiftVoucherCode() {
      const voucherCode = this.hopGiftVoucherCode;
      if (voucherCode != '' && voucherCode != null) {
        this.checkingVoucherCode = true;
        const query = {
          PropertyId: this.propertyId,
          LanguageCode: this.languageCode,
          VoucherCode: voucherCode,
          Rooms: this.reservationItems.map((ri) => ri.toDTO()),
        } as CheckVoucherRequestDTO;

        const checkVoucherCodePromise =
          apiService.reservationCheckVoucherCode(query);

        checkVoucherCodePromise.then((checkVoucherCodeResponse) => {
          if (checkVoucherCodeResponse.data != null) {
            if (checkVoucherCodeResponse.data.IsValid) {
              this.hopGiftDiscountAmount =
                checkVoucherCodeResponse.data.TotalDiscount;
              this.hopGiftMessage = checkVoucherCodeResponse.data.Message;
              this.hopGiftShowContactUs =
                checkVoucherCodeResponse.data.ShowExperienceWarningMessage;
            } else {
              this.hopGiftDiscountAmount = null;
              this.hopGiftMessage = checkVoucherCodeResponse.data.Message;

              Notify.create({
                color: 'negative',
                message: checkVoucherCodeResponse.data.Message,
              });
            }
          }
        });
        checkVoucherCodePromise.catch(() => {
          this.hopGiftDiscountAmount = null;
          this.hopGiftMessage = null;
          this.hopGiftShowContactUs = false;
        });

        checkVoucherCodePromise.finally(() => {
          this.checkingVoucherCode = false;
        });
      }
    },
  },
});
